import { Link } from "react-router-dom";
import { useState } from "react";
import { LIFESTYLE } from "@/data/products";
import { ArrowRight, FileText, BarChart3, Shield, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HIGHLIGHTS = [
  { icon: BarChart3, title: "Corporate Profile", desc: "Incorporated 6 July 2026 · Manchester HQ · 5 brand families in development" },
  { icon: FileText, title: "Growth Strategy", desc: "UK launch, international distribution partnerships and long-term brand equity." },
  { icon: Shield, title: "Corporate Governance", desc: "UK-registered company (No. 17323558). Transparent reporting and ESG principles." },
  { icon: Download, title: "Downloads", desc: "Annual reports, ESG data, investor presentations." },
];

export default function Investors() {
  const [form, setForm] = useState({ full_name: "", email: "", company: "", phone: "", enquiry_type: "General", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/investors/enquire", form);
      toast.success(data.message);
      setDone(true);
    } catch { toast.error("Please try again."); }
    setLoading(false);
  };

  return (
    <div data-testid="investors-page">
      <section className="relative pt-20 pb-24 bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={LIFESTYLE.handshake} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/40 to-neutral-950" />
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="eyebrow text-gold-400 mb-4">Investor Relations</div>
          <h1 className="font-display font-bold text-5xl lg:text-7xl tracking-tighter leading-[0.95]">Building a UK <span className="gold-gradient-text">beverage company.</span></h1>
          <p className="mt-6 max-w-2xl text-neutral-300">SBC Ltd is a UK-registered beverage startup incorporated on 6 July 2026 and based in Manchester. We are developing a portfolio of five brands — Maltora, OneCola, VoltX, OneFresh and OneSpring — with a focus on product development, brand innovation and manufacturing partnerships for the UK and international markets.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/portal/investor" className="inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-6 py-3 text-sm font-semibold">Investor Portal <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/downloads" className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white rounded-full px-6 py-3 text-sm font-semibold">Download Reports</Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: "2026", l: "Incorporated UK" },
            { n: "5", l: "Brand Families" },
            { n: "Manchester", l: "Headquarters" },
            { n: "17323558", l: "Company No." },
          ].map((s) => (
            <div key={s.l} className="p-6 rounded-2xl border border-border">
              <div className="font-display font-black text-4xl gold-gradient-text">{s.n}</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-muted/40">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-2 gap-4">
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="p-8 rounded-2xl bg-background border border-border">
              <div className="w-11 h-11 rounded-lg bg-gold-400/15 flex items-center justify-center mb-4">
                <h.icon className="w-5 h-5 text-gold-500" />
              </div>
              <h3 className="font-display font-bold text-2xl">{h.title}</h3>
              <p className="text-muted-foreground mt-2">{h.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 bg-background" data-testid="investor-enquiry-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="eyebrow">Investor Enquiries</div>
            <h2 className="font-display font-bold text-4xl tracking-tighter mt-2">Speak to our IR team</h2>
          </div>
          {done ? (
            <div className="p-10 rounded-2xl bg-muted/40 text-center" data-testid="investor-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-gold-400 text-neutral-950 flex items-center justify-center font-black text-xl">✓</div>
              <div className="font-display text-2xl mt-4">Enquiry received</div>
              <p className="text-muted-foreground mt-2">Our IR team will follow up shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-background rounded-2xl border border-border p-6 space-y-4" data-testid="investor-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <IF label="Full name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testId="investor-name" />
                <IF label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="investor-email" />
                <IF label="Company / Fund" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="investor-company" />
                <IF label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="investor-phone" />
              </div>
              <div>
                <IL>Enquiry Type</IL>
                <select value={form.enquiry_type} onChange={(e) => setForm({ ...form, enquiry_type: e.target.value })} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="investor-enquiry-type">
                  <option>General</option><option>Analyst / Research</option><option>Institutional</option><option>Family Office</option><option>Media / PR</option>
                </select>
              </div>
              <div>
                <IL>Message *</IL>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid="investor-message" />
              </div>
              <button disabled={loading} className="w-full bg-neutral-950 dark:bg-white dark:text-neutral-950 text-white rounded-full h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="investor-submit">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send Enquiry
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const IL = ({ children }) => <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{children}</div>;
const IF = ({ label, value, onChange, required, type = "text", testId }) => (
  <div>
    <IL>{label}</IL>
    <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400" data-testid={testId} />
  </div>
);
